import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import './Login.css';
import apiClient, { baseurl } from '../../api';

const STEP_CREDENTIALS = 'CREDENTIALS';
const STEP_MFA_VERIFY = 'MFA_VERIFY';
const STEP_MFA_ENROLL = 'MFA_ENROLL';
const STEP_MFA_RECOVERY_CODES = 'MFA_RECOVERY_CODES';

const Login = ({ setIsAuthenticated }) => {
  const navigate = useNavigate();

  // Authentication State
  const [step, setStep] = useState(STEP_CREDENTIALS);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [authMethod, setAuthMethod] = useState('totp'); // 'totp' | 'recovery'
  const [recoveryCode, setRecoveryCode] = useState('');
  const [error, setError] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showManualKey, setShowManualKey] = useState(false);

  // In-memory challenge data (never stored in persistent storage)
  const [challengeToken, setChallengeToken] = useState(null);
  const [confirmationToken, setConfirmationToken] = useState(null);
  const [provisioningUri, setProvisioningUri] = useState('');
  const [manualSecret, setManualSecret] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [pendingTokens, setPendingTokens] = useState(null);
  const [expiresIn, setExpiresIn] = useState(300);

  const timerRef = useRef(null);

  // Countdown timer for active challenges (TTL 300s)
  useEffect(() => {
    if ((step === STEP_MFA_VERIFY || step === STEP_MFA_ENROLL) && expiresIn > 0) {
      timerRef.current = setInterval(() => {
        setExpiresIn((prev) => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            setError('MFA challenge has expired. Please sign in again.');
            setChallengeToken(null);
            setConfirmationToken(null);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [step, expiresIn]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  // Clear stale credentials from previous sessions or unauthenticated visits
  const clearStaleCredentials = useCallback(() => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('authToken');
    localStorage.removeItem('bearer');
    localStorage.removeItem('lastVisitedRoute');
    if (typeof setIsAuthenticated === 'function') {
      setIsAuthenticated(false);
    }
  }, [setIsAuthenticated]);

  // Ensure unauthenticated state on mount
  useEffect(() => {
    clearStaleCredentials();
  }, [clearStaleCredentials]);

  // Helper to extract clean error message
  const extractErrorMessage = (err, defaultMsg = 'An error occurred. Please try again.') => {
    if (err.response?.data?.detail) {
      return err.response.data.detail;
    }
    if (err.response?.data?.message) {
      return err.response.data.message;
    }
    return defaultMsg;
  };

  // Step 1: Initial Credential Submission
  const handleCredentialLogin = async (e) => {
    e.preventDefault();
    setError('');
    setInfoMessage('');
    clearStaleCredentials();
    setIsLoading(true);

    const loginPayload = {
      email: email.trim(),
      password: password,
    };

    try {
      const response = await apiClient.post('accounts/login/internal/', loginPayload);
      const data = response.data;

      // Case A: Ordinary user / Non-MFA user login (direct tokens returned)
      if (data.access && data.refresh) {
        localStorage.setItem('accessToken', data.access);
        localStorage.setItem('refreshToken', data.refresh);
        setIsAuthenticated(true);
        navigate('/dashboard');
        return;
      }

      // Case B: SuperAdmin MFA Required
      if (data.mfa_required) {
        if (data.mfa_enrolled === false) {
          // Unenrolled SuperAdmin: initialize enrollment
          const enrollToken = data.enrollment_token;
          await startEnrollment(enrollToken);
        } else if (data.mfa_enrolled === true) {
          // Enrolled SuperAdmin: prompt for TOTP verification
          setChallengeToken(data.challenge_token);
          setExpiresIn(data.expires_in || 300);
          setTotpCode('');
          setRecoveryCode('');
          setAuthMethod('totp');
          setStep(STEP_MFA_VERIFY);
        } else {
          setError('Unexpected MFA state received. Please contact administrator.');
        }
      } else {
        setError('Invalid credentials');
      }
    } catch (err) {
      if (err.response?.status === 401) {
        setError(extractErrorMessage(err, 'Username or password is incorrect.'));
      } else if (err.response?.status === 503) {
        setError('Authentication service temporarily unavailable. Please try again later.');
      } else {
        setError(extractErrorMessage(err, 'Login failed. Please check your credentials.'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Helper: Request Enrollment Data from Backend
  const startEnrollment = async (enrollToken) => {
    try {
      const enrollRes = await apiClient.post('accounts/auth/mfa/enroll-init/', {
        enrollment_token: enrollToken,
      });
      const enrollData = enrollRes.data;

      setProvisioningUri(enrollData.provisioning_uri || '');
      setManualSecret(enrollData.secret || '');
      setConfirmationToken(enrollData.confirmation_token || enrollData.challenge_token || '');
      setExpiresIn(enrollData.expires_in || 300);
      setTotpCode('');
      setStep(STEP_MFA_ENROLL);
    } catch (err) {
      if (err.response?.status === 429) {
        setError(extractErrorMessage(err, 'Rate limit exceeded. Please try again later.'));
      } else {
        setError(extractErrorMessage(err, 'Failed to initialize MFA enrollment. Please try again.'));
      }
    }
  };

  // Step 2A: Verify 6-digit code for Enrolled SuperAdmin
  const handleTotpVerify = async (e) => {
    e.preventDefault();
    setError('');
    setInfoMessage('');

    if (expiresIn <= 0) {
      setError('MFA challenge has expired. Please sign in again.');
      return;
    }

    if (!totpCode || totpCode.trim().length !== 6) {
      setError('Please enter a valid 6-digit authenticator code.');
      return;
    }

    setIsLoading(true);

    try {
      const response = await apiClient.post('accounts/auth/mfa/totp-verify/', {
        challenge_token: challengeToken,
        totp_code: totpCode.trim(),
        device_name: 'SuperAdmin Web Console',
      });
      const data = response.data;

      if (data.access && data.refresh) {
        localStorage.setItem('accessToken', data.access);
        localStorage.setItem('refreshToken', data.refresh);
        // Clear memory state
        setChallengeToken(null);
        setIsAuthenticated(true);
        navigate('/dashboard');
      } else {
        setError('Failed to establish session. Please sign in again.');
      }
    } catch (err) {
      const status = err.response?.status;
      if (status === 400) {
        setError(extractErrorMessage(err, 'Invalid authentication code. Please check your authenticator app.'));
        setTotpCode('');
      } else if (status === 401) {
        setError(extractErrorMessage(err, 'MFA challenge expired or invalid. Please sign in again.'));
        setChallengeToken(null);
      } else if (status === 429) {
        setError(extractErrorMessage(err, 'Too many failed attempts. Account temporarily locked out.'));
      } else if (status === 503) {
        setError('Authentication service temporarily unavailable. Please try again later.');
      } else {
        setError(extractErrorMessage(err, 'Verification failed. Please try again.'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleRecoveryCodeChange = (e) => {
    const raw = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 16);
    const parts = [];
    for (let i = 0; i < raw.length; i += 4) {
      parts.push(raw.slice(i, i + 4));
    }
    setRecoveryCode(parts.join('-'));
  };

  // Step 2A-Alt: Verify Single-Use Recovery Code for Enrolled SuperAdmin
  const handleRecoveryVerify = async (e) => {
    e.preventDefault();
    setError('');
    setInfoMessage('');

    if (expiresIn <= 0) {
      setError('MFA challenge has expired. Please sign in again.');
      return;
    }

    const cleanCode = recoveryCode.trim();
    if (!cleanCode) {
      setError('Please enter your recovery code.');
      return;
    }

    setIsLoading(true);

    try {
      const response = await apiClient.post('accounts/auth/mfa/recovery-verify/', {
        challenge_token: challengeToken,
        recovery_code: cleanCode,
        device_name: 'SuperAdmin Web Console',
      });
      const data = response.data;

      if (data.access && data.refresh) {
        localStorage.setItem('accessToken', data.access);
        localStorage.setItem('refreshToken', data.refresh);
        // Clear memory state
        setChallengeToken(null);
        setRecoveryCode('');
        setIsAuthenticated(true);
        navigate('/dashboard');
      } else {
        setError('Failed to establish session. Please sign in again.');
      }
    } catch (err) {
      const status = err.response?.status;
      if (status === 400) {
        setError(extractErrorMessage(err, 'Invalid recovery code. Please check and try again.'));
        setRecoveryCode('');
      } else if (status === 401) {
        setError(extractErrorMessage(err, 'MFA challenge expired or invalid. Please sign in again.'));
        setChallengeToken(null);
        setRecoveryCode('');
      } else if (status === 429) {
        setError(extractErrorMessage(err, 'Too many failed attempts. Account temporarily locked out.'));
      } else if (status === 503) {
        setError('Authentication service temporarily unavailable. Please try again later.');
      } else {
        setError(extractErrorMessage(err, 'Recovery code verification failed. Please try again.'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Step 2B: Activate Enrollment for Unenrolled SuperAdmin
  const handleEnrollActivate = async (e) => {
    e.preventDefault();
    setError('');
    setInfoMessage('');

    if (expiresIn <= 0) {
      setError('Enrollment challenge has expired. Please sign in again.');
      return;
    }

    if (!totpCode || totpCode.trim().length !== 6) {
      setError('Please enter the 6-digit code shown in your authenticator app.');
      return;
    }

    setIsLoading(true);

    try {
      const response = await apiClient.post('accounts/auth/mfa/enroll-activate/', {
        confirmation_token: confirmationToken,
        totp_code: totpCode.trim(),
        device_name: 'SuperAdmin Web Console',
      });
      const data = response.data;

      // Keep tokens and recovery codes in memory ONLY. Do not persist yet.
      setRecoveryCodes(data.recovery_codes || []);
      setPendingTokens({
        access: data.access,
        refresh: data.refresh,
      });

      // Clear setup secret from memory
      setManualSecret('');
      setProvisioningUri('');
      setConfirmationToken(null);

      // Transition to recovery codes display
      setStep(STEP_MFA_RECOVERY_CODES);
    } catch (err) {
      const status = err.response?.status;
      if (status === 400) {
        setError(extractErrorMessage(err, 'Invalid authenticator code. Please try again.'));
        setTotpCode('');
      } else if (status === 401) {
        setError(extractErrorMessage(err, 'Enrollment challenge has expired or is invalid. Please sign in again.'));
        setConfirmationToken(null);
      } else if (status === 429) {
        setError(extractErrorMessage(err, 'Too many failed attempts. Please try again later.'));
      } else {
        setError(extractErrorMessage(err, 'Activation failed. Please try again.'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Step 3: Acknowledge Recovery Codes and Establish Session
  const handleAcknowledgeRecoveryCodes = () => {
    if (pendingTokens && pendingTokens.access && pendingTokens.refresh) {
      localStorage.setItem('accessToken', pendingTokens.access);
      localStorage.setItem('refreshToken', pendingTokens.refresh);
      // Clean up in-memory recovery codes and tokens
      setRecoveryCodes([]);
      setPendingTokens(null);
      setIsAuthenticated(true);
      navigate('/dashboard');
    } else {
      setError('Session could not be established. Please sign in again.');
      handleCancelMfa();
    }
  };

  // Copy Recovery Codes to Clipboard
  const handleCopyRecoveryCodes = () => {
    if (recoveryCodes.length > 0) {
      const text = recoveryCodes.join('\n');
      navigator.clipboard.writeText(text).then(() => {
        setInfoMessage('Recovery codes copied to clipboard!');
        setTimeout(() => setInfoMessage(''), 3000);
      });
    }
  };

  // Download Recovery Codes as .txt file
  const handleDownloadRecoveryCodes = () => {
    if (recoveryCodes.length > 0) {
      const text = `SALESROUTE SUPERADMIN RECOVERY CODES\nGenerated: ${new Date().toISOString()}\n\n` +
        `Keep these 10 one-time recovery codes in a secure location.\nEach code can only be used once.\n\n` +
        recoveryCodes.map((c, i) => `${i + 1}. ${c}`).join('\n') +
        `\n\nDO NOT SHARE THESE CODES WITH ANYONE.`;

      const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'salesroute-superadmin-recovery-codes.txt';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };

  // Cancel MFA and return safely to credential login
  const handleCancelMfa = () => {
    clearStaleCredentials();
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    setStep(STEP_CREDENTIALS);
    setChallengeToken(null);
    setConfirmationToken(null);
    setProvisioningUri('');
    setManualSecret('');
    setTotpCode('');
    setRecoveryCode('');
    setAuthMethod('totp');
    setRecoveryCodes([]);
    setPendingTokens(null);
    setError('');
    setInfoMessage('');
    setShowManualKey(false);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="registration-whole-container">
      <div className="sub-container-reg">
        <div className="reg-navbar">
          <div className="logo-container">
            <img
              src="assets/reg-nav-logo.png"
              alt="logo"
              className="reg-navbar-logo"
            />
          </div>

          <div className="country-nav">
            <img
              src="assets/country2.png"
              alt="country flag"
              className="country-image"
            />
            <select
              id="countrySelect"
              className="countries-name common-text fts-13 ftw-4 black"
            >
              <option>English</option>
              <option>Arabic</option>
              <option>Tamil</option>
            </select>
          </div>
        </div>
      </div>

      <div className="login-form-container">
        <div className={`login-form-sub-container ${step === STEP_MFA_RECOVERY_CODES || step === STEP_MFA_ENROLL ? 'recovery-container-width' : ''}`}>

          {/* STEP 1: Credential Login */}
          {step === STEP_CREDENTIALS && (
            <>
              <span className="black common-text ftw-6 welcome-back">Welcome Back</span>
              <p className="common-text fts-18 dk-gray">Login into your account</p>

              {error && (
                <p className="error-text red" role="alert">
                  {error}
                </p>
              )}

              <form onSubmit={handleCredentialLogin} autoComplete="off">
                <div className="login-input d-flex ai-center w-100 jc-sb login-password fts-14 common-text bg-white mt-30">
                  <input
                    className="w-100 fts-17 h-100 bg-trans"
                    type="email"
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isLoading}
                    required
                    autoComplete="off"
                    aria-label="Email"
                  />
                </div>

                <div className="login-input d-flex ai-center w-100 jc-sb login-password fts-14 common-text bg-white">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="h-100 w-100 fts-17"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isLoading}
                    required
                    autoComplete="off"
                    aria-label="Password"
                  />
                  {showPassword ? (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 576 512"
                      fill="rgb(160, 156, 156)"
                      xmlns="http://www.w3.org/2000/svg"
                      onClick={togglePasswordVisibility}
                      style={{ cursor: 'pointer' }}
                      aria-label="Hide password"
                    >
                      <path d="M572.52 241.4C518.29 135.13 407.65 64 288 64S57.71 135.13 3.48 241.4a48.6 48.6 0 0 0 0 29.2C57.71 376.87 168.35 448 288 448s230.29-71.13 284.52-177.4a48.6 48.6 0 0 0 0-29.2zM288 400c-79.4 0-160.54-46.55-207.62-128C127.46 190.55 208.6 144 288 144s160.54 46.55 207.62 128C448.54 353.45 367.4 400 288 400zm0-240a112 112 0 1 0 112 112A112 112 0 0 0 288 160z" />
                    </svg>
                  ) : (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 640 512"
                      fill="rgb(160, 156, 156)"
                      xmlns="http://www.w3.org/2000/svg"
                      onClick={togglePasswordVisibility}
                      style={{ cursor: 'pointer' }}
                      aria-label="Show password"
                    >
                      <path d="M38.8 5.1C28.4-3.1 13.3-1.2 5.1 9.2S-1.2 34.7 9.2 42.9l592 464c10.4 8.2 25.5 6.3 33.7-4.1s6.3-25.5-4.1-33.7L525.6 386.7c39.6-40.6 66.4-86.1 79.9-118.4c3.3-7.9 3.3-16.7 0-24.6c-14.9-35.7-46.2-87.7-93-131.1C465.5 68.8 400.8 32 320 32c-68.2 0-125 26.3-169.3 60.8L38.8 5.1zM223.1 149.5C248.6 126.2 282.7 112 320 112c79.5 0 144 64.5 144 144c0 24.9-6.3 48.3-17.4 68.7L408 294.5c8.4-19.3 10.6-41.4 4.8-63.3c-11.1-41.5-47.8-69.4-88.6-71.1c-5.8-.2-9.2 6.1-7.4 11.7c2.1 6.4 3.3 13.2 3.3 20.3c0 10.2-2.4 19.8-6.6 28.3l-90.3-70.8zM373 389.9c-16.4 6.5-34.3 10.1-53 10.1c-79.5 0-144-64.5-144-144c0-6.9 .5-13.6 1.4-20.2L83.1 161.5C60.3 191.2 44 220.8 34.5 243.7c-3.3 7.9-3.3 16.7 0 24.6c14.9 35.7 46.2 87.7 93 131.1C174.5 443.2 239.2 480 320 480c47.8 0 89.9-12.9 126.2-32.5L373 389.9z" />
                    </svg>
                  )}
                </div>

                <button
                  type="submit"
                  className="login-input common-text fts-18 ftw-4 white bg-dk-blue"
                  disabled={isLoading}
                >
                  {isLoading ? 'Signing in...' : 'Log in'}
                </button>
                <span
                  className="forgot-password"
                  onClick={() => (window.location.href = `${baseurl}reset-password-form/?source=internal`)}
                  style={{ cursor: 'pointer', color: 'gray', marginTop: '10px' }}
                >
                  Forgot password ?
                </span>
              </form>
            </>
          )}

          {/* STEP 2A: Enrolled SuperAdmin MFA Verification (TOTP or Recovery Code) */}
          {step === STEP_MFA_VERIFY && (
            <div className="mfa-step-container">
              <span className="black common-text ftw-6 welcome-back">Two-Factor Authentication</span>
              {authMethod === 'totp' ? (
                <>
                  <p className="common-text fts-15 dk-gray mt-10">
                    Enter the 6-digit code generated by your Google Authenticator or compatible app.
                  </p>

                  {expiresIn > 0 ? (
                    <div className="mfa-timer-badge">
                      <span>Expires in: </span>
                      <strong>{formatTime(expiresIn)}</strong>
                    </div>
                  ) : (
                    <p className="error-text red" role="alert">Challenge expired. Please return to login.</p>
                  )}

                  {error && (
                    <p className="error-text red mt-10" role="alert">
                      {error}
                    </p>
                  )}

                  <form onSubmit={handleTotpVerify} autoComplete="off" className="mt-20">
                    <div className="login-input d-flex ai-center w-100 jc-sb fts-14 common-text bg-white mfa-code-input-container">
                      <input
                        className="w-100 fts-20 h-100 bg-trans text-center mfa-code-input"
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength="6"
                        placeholder="000000"
                        value={totpCode}
                        onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        disabled={isLoading || expiresIn <= 0}
                        required
                        autoFocus
                        autoComplete="one-time-code"
                        aria-label="6-digit authenticator code"
                      />
                    </div>

                    <button
                      type="submit"
                      className="login-input common-text fts-18 ftw-4 white bg-dk-blue mt-15"
                      disabled={isLoading || totpCode.length !== 6 || expiresIn <= 0}
                    >
                      {isLoading ? 'Verifying...' : 'Verify & Sign In'}
                    </button>

                    <div className="mfa-switch-mode-container mt-15">
                      <button
                        type="button"
                        className="mfa-toggle-mode-btn common-text fts-13"
                        onClick={() => {
                          setAuthMethod('recovery');
                          setError('');
                          setInfoMessage('');
                        }}
                        disabled={isLoading}
                      >
                        Lost access to authenticator? Use a recovery code
                      </button>
                    </div>

                    <button
                      type="button"
                      className="mfa-cancel-btn common-text fts-14"
                      onClick={handleCancelMfa}
                      disabled={isLoading}
                    >
                      ← Back to Login
                    </button>
                  </form>
                </>
              ) : (
                <>
                  <p className="common-text fts-15 dk-gray mt-10">
                    Enter one of your 16-character emergency recovery codes (format: XXXX-XXXX-XXXX-XXXX).
                  </p>

                  {expiresIn > 0 ? (
                    <div className="mfa-timer-badge">
                      <span>Expires in: </span>
                      <strong>{formatTime(expiresIn)}</strong>
                    </div>
                  ) : (
                    <p className="error-text red" role="alert">Challenge expired. Please return to login.</p>
                  )}

                  {error && (
                    <p className="error-text red mt-10" role="alert">
                      {error}
                    </p>
                  )}

                  <form onSubmit={handleRecoveryVerify} autoComplete="off" className="mt-20">
                    <div className="login-input d-flex ai-center w-100 jc-sb fts-14 common-text bg-white mfa-recovery-input-container">
                      <input
                        className="w-100 fts-16 h-100 bg-trans text-center mfa-recovery-code-input"
                        type="text"
                        placeholder="XXXX-XXXX-XXXX-XXXX"
                        maxLength="19"
                        value={recoveryCode}
                        onChange={handleRecoveryCodeChange}
                        disabled={isLoading || expiresIn <= 0}
                        required
                        autoFocus
                        autoCapitalize="characters"
                        autoComplete="off"
                        aria-label="16-character recovery code"
                      />
                    </div>

                    <button
                      type="submit"
                      className="login-input common-text fts-18 ftw-4 white bg-dk-blue mt-15"
                      disabled={isLoading || recoveryCode.replace(/-/g, '').length !== 16 || expiresIn <= 0}
                    >
                      {isLoading ? 'Verifying...' : 'Verify Recovery Code & Sign In'}
                    </button>

                    <div className="mfa-switch-mode-container mt-15">
                      <button
                        type="button"
                        className="mfa-toggle-mode-btn common-text fts-13"
                        onClick={() => {
                          setAuthMethod('totp');
                          setError('');
                          setInfoMessage('');
                        }}
                        disabled={isLoading}
                      >
                        ← Back to Authenticator App code
                      </button>
                    </div>

                    <button
                      type="button"
                      className="mfa-cancel-btn common-text fts-14"
                      onClick={handleCancelMfa}
                      disabled={isLoading}
                    >
                      ← Back to Login
                    </button>
                  </form>
                </>
              )}
            </div>
          )}

          {/* STEP 2B: Unenrolled SuperAdmin Initial Enrollment */}
          {step === STEP_MFA_ENROLL && (
            <div className="mfa-step-container">
              <span className="black common-text ftw-6 welcome-back">Set Up Two-Factor Authentication</span>
              <p className="common-text fts-14 dk-gray mt-5">
                Two-Factor Authentication is mandatory for SuperAdmin accounts. Scan the QR code with Google Authenticator or a compatible app.
              </p>

              {expiresIn > 0 ? (
                <div className="mfa-timer-badge">
                  <span>Expires in: </span>
                  <strong>{formatTime(expiresIn)}</strong>
                </div>
              ) : (
                <p className="error-text red" role="alert">Enrollment challenge expired. Please return to login.</p>
              )}

              {error && (
                <p className="error-text red mt-10" role="alert">
                  {error}
                </p>
              )}

              {provisioningUri ? (
                <div className="mfa-qr-card mt-15">
                  <div className="mfa-qr-wrapper">
                    <QRCodeSVG
                      value={provisioningUri}
                      size={180}
                      level="M"
                      includeMargin={true}
                    />
                  </div>

                  <div className="mfa-manual-key-section mt-10">
                    <button
                      type="button"
                      className="mfa-toggle-manual-btn common-text fts-12"
                      onClick={() => setShowManualKey(!showManualKey)}
                    >
                      {showManualKey ? 'Hide manual key' : "Can't scan QR code? Enter manual key"}
                    </button>

                    {showManualKey && manualSecret && (
                      <div className="mfa-manual-secret-box mt-5">
                        <span className="fts-11 dk-gray">Account Key (Base32):</span>
                        <code className="mfa-secret-code">{manualSecret}</code>
                        <button
                          type="button"
                          className="mfa-copy-btn fts-12"
                          onClick={() => {
                            navigator.clipboard.writeText(manualSecret);
                            setInfoMessage('Key copied to clipboard!');
                            setTimeout(() => setInfoMessage(''), 3000);
                          }}
                        >
                          Copy Key
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="mfa-loading-box mt-20">Initializing enrollment...</div>
              )}

              {infoMessage && <p className="success-text green mt-5">{infoMessage}</p>}

              {/* Pre-Activation Warning: Recovery Codes Lifecycle */}
              <div className="mfa-pre-activation-warning mt-15" role="alert">
                <div className="warning-title">⚠️ Important: Read Before Activating</div>
                <ul className="warning-list fts-12">
                  <li>Upon activation, you will be given <strong>10 one-time recovery codes</strong> displayed <strong>only once</strong>.</li>
                  <li>You must save or download these recovery codes immediately. Closing, refreshing, or navigating away before saving them will <strong>permanently lose</strong> them.</li>
                  <li>Lost recovery codes <strong>cannot be recovered or regenerated</strong> by the system.</li>
                  <li>Please ensure your authenticator app is working and you can generate 6-digit codes before proceeding.</li>
                </ul>
              </div>

              <form onSubmit={handleEnrollActivate} autoComplete="off" className="mt-15">
                <label className="common-text fts-13 ftw-5 dk-gray d-block text-left mb-5">
                  Enter the 6-digit code shown in your app to activate:
                </label>
                <div className="login-input d-flex ai-center w-100 jc-sb fts-14 common-text bg-white mfa-code-input-container">
                  <input
                    className="w-100 fts-20 h-100 bg-trans text-center mfa-code-input"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength="6"
                    placeholder="000000"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    disabled={isLoading || expiresIn <= 0}
                    required
                    autoComplete="one-time-code"
                    aria-label="6-digit activation code"
                  />
                </div>

                <button
                  type="submit"
                  className="login-input common-text fts-18 ftw-4 white bg-dk-blue mt-15"
                  disabled={isLoading || totpCode.length !== 6 || expiresIn <= 0}
                >
                  {isLoading ? 'Activating...' : 'Activate & Continue'}
                </button>

                <button
                  type="button"
                  className="mfa-cancel-btn common-text fts-14"
                  onClick={handleCancelMfa}
                  disabled={isLoading}
                >
                  ← Cancel & Return to Login
                </button>
              </form>
            </div>
          )}

          {/* STEP 3: Mandatory One-Time Recovery Codes Screen */}
          {step === STEP_MFA_RECOVERY_CODES && (
            <div className="mfa-step-container text-center">
              <span className="black common-text ftw-6 welcome-back">Save Your Recovery Codes</span>
              <div className="mfa-warning-banner mt-10">
                <strong>IMPORTANT:</strong> These 10 recovery codes are displayed <u>ONLY ONCE</u>. If you lose access to your authenticator app, these are the only way to recover your SuperAdmin account.
              </div>

              {infoMessage && <p className="success-text green mt-10">{infoMessage}</p>}
              {error && <p className="error-text red mt-10" role="alert">{error}</p>}

              <div className="mfa-recovery-grid mt-15">
                {recoveryCodes.map((code, index) => (
                  <div key={index} className="mfa-recovery-code-card">
                    <span className="code-index">{index + 1}.</span>
                    <span className="code-value">{code}</span>
                  </div>
                ))}
              </div>

              <div className="mfa-action-row mt-15">
                <button
                  type="button"
                  className="mfa-secondary-action-btn common-text fts-13"
                  onClick={handleCopyRecoveryCodes}
                >
                  📋 Copy All Codes
                </button>
                <button
                  type="button"
                  className="mfa-secondary-action-btn common-text fts-13"
                  onClick={handleDownloadRecoveryCodes}
                >
                  💾 Download as .txt
                </button>
              </div>

              <div className="mt-25">
                <button
                  type="button"
                  className="login-input common-text fts-16 ftw-5 white bg-dk-blue mfa-confirm-btn"
                  onClick={handleAcknowledgeRecoveryCodes}
                >
                  I have saved my recovery codes — Continue to Dashboard
                </button>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default Login;